/**
 * The creator's drafts, as the studio holds them.
 *
 * Two sources, one list. The records live on the creator's PDS and are read by
 * author, which is the whole reason unfinished work follows a creator between
 * devices; this device's index is merged in so the list is instant on a cold
 * start and still there when the network is not.
 *
 * The rules live here rather than in the screen because they are about the
 * *transition* between the two sources, and a screen that owns five other pieces
 * of state is the wrong place to look for them:
 *
 *  - **A server that answers, wins.** Its rows overwrite this device's on a
 *    shared uri, because it is the only thing that knows a draft was renamed,
 *    published or deleted somewhere else — and a row this device holds but the
 *    server does not list is *kept*, not pruned, so a save made a moment ago is
 *    never hidden while the AppView catches up.
 *  - **A server that cannot be asked, changes nothing.** A failure is not an
 *    empty list: no rows are dropped and the creator sees the device index
 *    exactly as it was. Two different failures take that path, and both are
 *    real: the network being down (the query rejects) and a deployment that does
 *    not serve the method yet (`useActorFlashbookDraftsQuery` answers `[]` for
 *    501 and 404 rather than throwing, so the feature switches itself on when
 *    the backend lands instead of looking like a wiped list).
 *
 * The list is deliberately *not* cleared while the request is in flight either:
 * what is on screen is this device's index from the first frame, and the server
 * replaces it when it has something to say. There is no spinner over the list,
 * because the list is already true.
 */
import {useEffect, useState} from 'react'

import {
  type FlashBookDraftRef,
  getFlashBookDrafts,
  syncFlashBookDrafts,
} from '#/lib/flashbookDrafts'
import {useActorFlashbookDraftsQuery} from '#/state/queries/flashbook'

export function useFlashBookDrafts(actor: string | undefined) {
  const [drafts, setDrafts] = useState<FlashBookDraftRef[]>(() =>
    getFlashBookDrafts(),
  )
  const remote = useActorFlashbookDraftsQuery(actor)

  useEffect(() => {
    // Only a *successful* answer is allowed to move the list. `isSuccess` and not
    // "has data": an errored query that happens to hold an empty array from an
    // earlier attempt must never be read as "the creator has no drafts".
    if (!remote.isSuccess || !remote.data) return
    setDrafts(syncFlashBookDrafts(remote.data))
  }, [remote.isSuccess, remote.data])

  return {drafts, setDrafts}
}
